// `/forgot-password` (UX-6a) — the request half of the password reset
// (spec 16 §3.4, §3.5a; contract: POST /v1/auth/password/forgot).
//
// THE DOOR IS SUCCESS-SHAPED BY CONTRACT: the server answers 202 for unknown
// emails, throttled requests and unverified accounts alike (no user oracle),
// so this screen has exactly one post-submit state — "if an account exists, a
// link is on its way" — and the copy says the conditional out loud rather than
// pretending to confirm an account. The only error branch is a 400 validation
// refusal (a malformed email that slipped the input's own check).

import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { client } from "../lib/api";
import { Banner } from "../components/toast";
import { Button } from "../components/button";
import { AdminForgotPasswordScreen } from "../areas/admin/screens/forgot-password";
import { isAdminHost } from "../lib/host";

/**
 * This path exists on BOTH kinds of host (D-587), like `/login`: a tenant host
 * serves the factory user's forgot-password request (spec 16 §3.4), and `admin.<apex>` serves
 * the platform staff one (spec 15 §3.1 step 7), whose API answers on that host
 * alone. No hook runs above the branch, so each side keeps its own hook order.
 */
export function ForgotPasswordPage() {
  if (isAdminHost(window.location.hostname)) return <AdminForgotPasswordScreen />;
  return <TenantForgotPasswordPage />;
}

function TenantForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const request = useMutation({
    mutationFn: async () => {
      const { error } = await client.POST("/v1/auth/password/forgot", {
        body: { email },
      });
      if (error) throw new Error("refused");
      return undefined;
    },
    onSuccess: () => {
      setSent(true);
    },
  });

  return (
    <section className="auth-card" data-testid="forgot-password">
      <h1 className="auth-title">{t("auth.forgotTitle")}</h1>
      <p className="auth-lead">{t("auth.forgotLead")}</p>

      {sent ? (
        <>
          <Banner kind="ok" data-testid="forgot-sent">
            {t("auth.forgotSent")}
          </Banner>
          <div className="auth-links">
            <Link to="/login" className="ds-lnk">
              {t("auth.backToSignIn")}
            </Link>
          </div>
        </>
      ) : (
        <>
          {request.isError ? <Banner kind="bad">{t("auth.forgotError")}</Banner> : null}
          <form
            className="auth-form"
            onSubmit={(e) => {
              e.preventDefault();
              request.mutate();
            }}
          >
            <div className="ds-field">
              <label htmlFor="forgot-email">{t("login.email")}</label>
              <input
                id="forgot-email"
                name="email"
                className="ds-input"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                required
              />
            </div>
            <div className="auth-actions">
              <Button
                type="submit"
                variant="primary"
                busy={request.isPending}
                busyLabel={t("auth.forgotSubmitting")}
              >
                {t("auth.forgotSubmit")}
              </Button>
              <Link to="/login" className="ds-lnk">
                {t("auth.backToSignIn")}
              </Link>
            </div>
          </form>
        </>
      )}
    </section>
  );
}
